import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { BrandingProvider } from './contexts/BrandingContext';
import { TenantProvider } from './contexts/TenantContext';
import { FormConfigProvider } from './contexts/FormConfigContext';
import { MOMFormConfigProvider } from './contexts/MOMFormConfigContext';
import { FileUploadConfigProvider } from './contexts/FileUploadConfigContext';
import PrivateRoute from './components/auth/PrivateRoute';
// Stylesheets
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './styles/main.css';
// Critical components - loaded immediately
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import ContactPage from './pages/ContactPage';
import NuCallWidget from './components/nucall/NuCallWidget';
// Lazy-loaded components - loaded on demand for better performance
const Register = lazy(() => import('./pages/Register'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const AISearchPage = lazy(() => import('./pages/AISearchPage'));
const KBPage = lazy(() => import('./pages/KB/KBPage'));
const TransactionsPage = lazy(() => import('./pages/TransactionsPage'));
const CreateTransactionPage = lazy(() => import('./pages/CreateTransactionPage'));
const TransactionDetailPage = lazy(() => import('./pages/TransactionDetailPage'));
const TransactionEditPage = lazy(() => import('./pages/TransactionEditPage'));
const UsersPage = lazy(() => import('./pages/UsersPage'));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const ReportBuilderPage = lazy(() => import('./pages/reports/ReportBuilderPage'));
const BudgetsPage = lazy(() => import('./pages/reports/BudgetsPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const AssignmentsPage = lazy(() => import('./pages/AssignmentsPage'));
const EmailTemplatesPage = lazy(() => import('./pages/EmailTemplatesPage'));
const AuditLogsPage = lazy(() => import('./pages/AuditLogsPage'));
const AssignedTasksPage = lazy(() => import('./pages/AssignedTasksPage'));
const ImportWizardPage = lazy(() => import('./pages/ImportWizardPage'));
const DraftsPage = lazy(() => import('./pages/DraftsPage'));
const TemplateListPage = lazy(() => import('./pages/TemplateListPage'));
const TemplateBuilderPage = lazy(() => import('./pages/TemplateBuilderPage'));
const DocumentListPage = lazy(() => import('./pages/DocumentListPage'));
const DocumentCreatePage = lazy(() => import('./pages/DocumentCreatePage'));
const DocumentDetailPage = lazy(() => import('./pages/DocumentDetailPage'));
const DocumentEditPage = lazy(() => import('./pages/DocumentEditPage'));
const PublicTransactionPage = lazy(() => import('./pages/PublicTransactionPage'));
const MyTeamPage = lazy(() => import('./pages/MyTeamPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const ProjectsPage = lazy(() => import('./pages/ProjectsPage'));
const CreateProjectPage = lazy(() => import('./pages/CreateProjectPage'));
const ProjectDetailPage = lazy(() => import('./pages/ProjectDetailPage'));
const ProjectEditPage = lazy(() => import('./pages/ProjectEditPage'));
const PublicProjectPage = lazy(() => import('./pages/PublicProjectPage'));
const PublicProjectUpdatePage = lazy(() => import('./pages/PublicProjectUpdatePage'));
const MOMsPage = lazy(() => import('./pages/MOMsPage'));
const MeetingsPage = lazy(() => import('./pages/Meetings/MeetingsPage'));
const CreateMOMPage = lazy(() => import('./pages/CreateMOMPage'));
const MOMDetailPage = lazy(() => import('./pages/MOMDetailPage'));
const MOMEditPage = lazy(() => import('./pages/MOMEditPage'));
const PublicMOMPage = lazy(() => import('./pages/PublicMOMPage'));
const MOMApprovalDashboard = lazy(() => import('./pages/MOMApprovalDashboard'));
const ExternalMOMApproval = lazy(() => import('./pages/ExternalMOMApproval'));
// Proposal Management
const ProposalDesignTemplatesPage = lazy(() => import('./pages/ProposalDesignTemplatesPage'));
const ProposalTemplatesPage = lazy(() => import('./pages/ProposalTemplatesPage'));
const ProposalTemplateBuilderPage = lazy(() => import('./pages/ProposalTemplateBuilderPage'));
const ProposalListPage = lazy(() => import('./pages/ProposalListPage'));
const ProposalFormPage = lazy(() => import('./pages/ProposalFormPage'));
const ProposalWizardPage = lazy(() => import('./pages/ProposalWizardPage'));
const ProposalDetailPage = lazy(() => import('./pages/ProposalDetailPage'));
// Payment Management
const PaymentListPage = lazy(() => import('./pages/PaymentListPage'));
const PaymentDetailPage = lazy(() => import('./pages/PaymentDetailPage'));
const PaymentCallbackPage = lazy(() => import('./pages/PaymentCallbackPage'));
const PublicPaymentPage = lazy(() => import('./pages/PublicPaymentPage'));
const PublicInvoicePage = lazy(() => import('./pages/PublicInvoicePage'));
// PR 5 — manual payment verification queue + bank accounts settings.
const PendingVerificationPage = lazy(() => import('./pages/payments/PendingVerificationPage'));
const BankAccountsPage = lazy(() => import('./pages/settings/BankAccountsPage'));
// PR 6 — Expenses module.
const ExpenseListPage = lazy(() => import('./pages/expenses/ExpenseListPage'));
const ExpenseCreatePage = lazy(() => import('./pages/expenses/ExpenseCreatePage'));
const ExpenseDetailPage = lazy(() => import('./pages/expenses/ExpenseDetailPage'));
// PR 8 — AI expense import.
const ExpenseImportPage = lazy(() => import('./pages/expenses/ExpenseImportPage'));
// Income module — incoming-money ledger (mirror of expenses).
const IncomeListPage = lazy(() => import('./pages/income/IncomeListPage'));
const IncomeCreatePage = lazy(() => import('./pages/income/IncomeCreatePage'));
const IncomeDetailPage = lazy(() => import('./pages/income/IncomeDetailPage'));
// PR 7 — Financials dashboard.
const PlatformDashboardPage = lazy(() => import('./pages/financials/PlatformDashboardPage'));
const ProjectFinancialsPage = lazy(() => import('./pages/financials/ProjectFinancialsPage'));
const LegalPage = lazy(() => import('./pages/legal/LegalPage'));
const TapPaymentSettingsPage = lazy(() => import('./pages/settings/TapPaymentSettingsPage'));
// InvoiceSettingsPage was folded into System Settings as a tab — see
// components/settings/InvoicingSettingsTab. The standalone page was
// removed; the route below redirects to /settings?tab=invoicing so
// existing bookmarks land on the right tab.
const InvoiceListPage = lazy(() => import('./pages/invoicing/InvoiceListPage'));
const InvoiceCreatePage = lazy(() => import('./pages/invoicing/InvoiceCreatePage'));
const InvoiceDetailPage = lazy(() => import('./pages/invoicing/InvoiceDetailPage'));
const MyInvoicesPage = lazy(() => import('./pages/invoicing/MyInvoicesPage'));

// Loading fallback component
const LoadingFallback = () => (
  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
    <div className="text-center">
      <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
        <span className="visually-hidden">Loading...</span>
      </div>
      <p className="mt-3 text-muted">Loading...</p>
    </div>
  </div>
);

function App() {
  return (
    <Router>
      <TenantProvider>
        <LanguageProvider>
          <BrandingProvider>
            <AuthProvider>
            {/* Voice assistant. Mounted here, not in Layout: Layout renders per
                page, so a route change would unmount it mid-call. */}
            <NuCallWidget />
            <FileUploadConfigProvider>
              <FormConfigProvider>
                <MOMFormConfigProvider>
                <Suspense fallback={<LoadingFallback />}>
                <Routes>
          {/* Public routes - no authentication required */}
          <Route path="/public/transactions/:transactionId" element={<PublicTransactionPage />} />
          <Route path="/public/projects/:projectId" element={<PublicProjectPage />} />
          {/* QR codes generated before 2026-10-28 encode /projects/public/<id>. */}
          <Route path="/projects/public/:projectId" element={<PublicProjectPage />} />
          <Route path="/public/updates/:token" element={<PublicProjectUpdatePage />} />
          <Route path="/public/moms/:momId" element={<PublicMOMPage />} />
          <Route path="/public/payments/:token" element={<PublicPaymentPage />} />
          <Route path="/public/invoices/:token" element={<PublicInvoicePage />} />
          <Route path="/payments/callback" element={<PaymentCallbackPage />} />

          {/* Tenant TAP settings (authenticated). */}
          <Route
            path="/settings/payments/tap"
            element={
              <PrivateRoute roles={['Admin']}>
                <TapPaymentSettingsPage />
              </PrivateRoute>
            }
          />

          {/* Invoicing module (ZATCA Phase-1). Sidebar gates these on
              `user.can_access_invoicing`; backend ViewSet enforces the
              same permission via IsAdminUser + org-scoped queryset.
              Settings live under /settings as a tab now; legacy URL
              redirects to the right tab via query param. */}
          <Route
            path="/settings/invoicing"
            element={<Navigate to="/settings?tab=invoicing" replace />}
          />
          <Route
            path="/invoices"
            element={<PrivateRoute><InvoiceListPage /></PrivateRoute>}
          />
          <Route
            path="/invoices/new"
            element={<PrivateRoute><InvoiceCreatePage /></PrivateRoute>}
          />
          <Route
            path="/invoices/:invoiceId/edit"
            element={<PrivateRoute><InvoiceCreatePage /></PrivateRoute>}
          />
          <Route
            path="/invoices/:invoiceId"
            element={<PrivateRoute><InvoiceDetailPage /></PrivateRoute>}
          />
          {/* Client-facing invoice list — client role + payments access. */}
          <Route
            path="/my-invoices"
            element={
              <PrivateRoute roles={['Client']} permission="can_access_payments">
                <MyInvoicesPage />
              </PrivateRoute>
            }
          />

          <Route path="/moms/external/:token" element={<ExternalMOMApproval />} />

          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          {/* Public legal pages. */}
          <Route path="/terms" element={<LegalPage kind="terms" />} />
          <Route path="/privacy" element={<LegalPage kind="privacy" />} />

          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
          <Route
            path="/dashboard" 
            element={
              <PrivateRoute>
                <Dashboard />
              </PrivateRoute>
            } 
          />
          <Route
            path="/transactions"
            element={
              <PrivateRoute roles={['Admin', 'Editor', 'Client']} permission="can_access_applications">
                <TransactionsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/transactions/create"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_applications">
                <CreateTransactionPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/transactions/:id"
            element={
              <PrivateRoute roles={['Admin', 'Editor', 'Client']} permission="can_access_applications">
                <TransactionDetailPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/transactions/:id/edit"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_applications">
                <TransactionEditPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/projects"
            element={
              <PrivateRoute roles={['Admin', 'Editor', 'Client']} permission="can_access_projects">
                <ProjectsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/projects/new"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_projects">
                <CreateProjectPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/projects/:id"
            element={
              <PrivateRoute roles={['Admin', 'Editor', 'Client']} permission="can_access_projects">
                <ProjectDetailPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/projects/:id/edit"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_projects">
                <ProjectEditPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/moms"
            element={
              <PrivateRoute roles={['Admin', 'Editor', 'Client']} permission="can_access_mom">
                <MOMsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/meetings"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_mom">
                <MeetingsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/moms/new"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_mom">
                <CreateMOMPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/moms/:id"
            element={
              <PrivateRoute roles={['Admin', 'Editor', 'Client']} permission="can_access_mom">
                <MOMDetailPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/moms/:id/edit"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_mom">
                <MOMEditPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/moms/approvals"
            element={
              <PrivateRoute roles={['Admin', 'Editor', 'Client']} permission="can_access_mom">
                <MOMApprovalDashboard />
              </PrivateRoute>
            }
          />
          <Route
            path="/users" 
            element={
              <PrivateRoute roles={['Admin']}>
                <UsersPage />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/notifications" 
            element={
              <PrivateRoute>
                <NotificationsPage />
              </PrivateRoute>
            } 
          />
          <Route
            path="/reports"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_reports">
                <ReportsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/reports/builder"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_reports">
                <ReportBuilderPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/reports/budgets"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_reports">
                <BudgetsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <PrivateRoute permission="can_access_settings">
                <SettingsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/assignments" 
            element={
              <PrivateRoute roles={['Admin']}>
                <AssignmentsPage />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/email-templates" 
            element={
              <PrivateRoute roles={['Admin']}>
                <EmailTemplatesPage />
              </PrivateRoute>
            } 
          />
          <Route 
            path="/audit-logs" 
            element={
              <PrivateRoute roles={['Admin']}>
                <AuditLogsPage />
              </PrivateRoute>
            } 
          />
          <Route
            path="/assigned-tasks"
            element={
              <PrivateRoute roles={['Editor']} permission="can_access_applications">
                <AssignedTasksPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/import-wizard"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_applications">
                <ImportWizardPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/drafts"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_applications">
                <DraftsPage />
              </PrivateRoute>
            }
          />
          {/* Document Management Routes */}
          <Route
            path="/documents/templates/create"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_documents">
                <TemplateBuilderPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/documents/templates/:id/edit"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_documents">
                <TemplateBuilderPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/documents/templates"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_documents">
                <TemplateListPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/documents/create"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_documents">
                <DocumentCreatePage />
              </PrivateRoute>
            }
          />
          <Route
            path="/documents/:id/edit"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_documents">
                <DocumentEditPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/documents/:id"
            element={
              <PrivateRoute roles={['Admin', 'Editor', 'Client']} permission="can_access_documents">
                <DocumentDetailPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/documents"
            element={
              <PrivateRoute roles={['Admin', 'Editor', 'Client']} permission="can_access_documents">
                <DocumentListPage />
              </PrivateRoute>
            }
          />
          {/* Proposal Management Routes */}
          <Route
            path="/proposals/designs"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_proposals">
                <ProposalDesignTemplatesPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/proposals/templates/create"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_proposals">
                <ProposalTemplateBuilderPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/proposals/templates/:id/edit"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_proposals">
                <ProposalTemplateBuilderPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/proposals/templates/:id"
            element={<Navigate to="edit" replace />}
          />
          <Route
            path="/proposals/templates"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_proposals">
                <ProposalTemplatesPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/proposals/new"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_proposals">
                <ProposalWizardPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/proposals/create"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_proposals">
                <ProposalFormPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/proposals/:id/edit"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_proposals">
                <ProposalFormPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/proposals/:id"
            element={
              <PrivateRoute roles={['Admin', 'Editor', 'Client']} permission="can_access_proposals">
                <ProposalDetailPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/proposals"
            element={
              <PrivateRoute roles={['Admin', 'Editor', 'Client']} permission="can_access_proposals">
                <ProposalListPage />
              </PrivateRoute>
            }
          />
          {/* AI Search (NuRag-backed). Open to all logged-in users — backend RBAC enforces row scoping. */}
          <Route
            path="/ai-search"
            element={
              <PrivateRoute roles={['Admin', 'Editor', 'Client']}>
                <AISearchPage />
              </PrivateRoute>
            }
          />
          {/* Knowledge Base — admin + editor only; backend rejects clients. */}
          <Route
            path="/kb"
            element={
              <PrivateRoute roles={['Admin', 'Editor']}>
                <KBPage />
              </PrivateRoute>
            }
          />
          {/* Payment Management Routes */}
          {/* /payments/pending-verification MUST come before /payments/:id —
              react-router matches dynamic segments greedily otherwise. */}
          <Route
            path="/payments/pending-verification"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_verify_payments">
                <PendingVerificationPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/settings/bank-accounts"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_payments">
                <BankAccountsPage />
              </PrivateRoute>
            }
          />

          {/* PR 6 — Expenses routes. /expenses/import + /expenses/new must
              precede /:id so react-router doesn't try them as dynamic ids. */}
          {/* PR 8 — AI Import. Gated by can_use_ai_expense_import (plan flag). */}
          <Route
            path="/expenses/import"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_expenses">
                <ExpenseImportPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/expenses/new"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_expenses">
                <ExpenseCreatePage />
              </PrivateRoute>
            }
          />
          <Route
            path="/expenses/:id/edit"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_expenses">
                <ExpenseCreatePage />
              </PrivateRoute>
            }
          />
          <Route
            path="/expenses/:id"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_expenses">
                <ExpenseDetailPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/expenses"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_expenses">
                <ExpenseListPage />
              </PrivateRoute>
            }
          />

          {/* Income routes. /income/new must precede /:id so react-router
              doesn't try it as a dynamic id. */}
          <Route
            path="/income/new"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_income">
                <IncomeCreatePage />
              </PrivateRoute>
            }
          />
          <Route
            path="/income/:id/edit"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_income">
                <IncomeCreatePage />
              </PrivateRoute>
            }
          />
          <Route
            path="/income/:id"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_income">
                <IncomeDetailPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/income"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_income">
                <IncomeListPage />
              </PrivateRoute>
            }
          />

          {/* PR 7 — Financials routes. Dashboard + per-project drill-in. */}
          <Route
            path="/financials"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_financial_reports">
                <PlatformDashboardPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/projects/:id/financials"
            element={
              <PrivateRoute roles={['Admin', 'Editor']} permission="can_access_financial_reports">
                <ProjectFinancialsPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/payments/:id"
            element={
              <PrivateRoute roles={['Admin', 'Editor', 'Client']} permission="can_access_payments">
                <PaymentDetailPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/payments"
            element={
              <PrivateRoute roles={['Admin', 'Editor', 'Client']} permission="can_access_payments">
                <PaymentListPage />
              </PrivateRoute>
            }
          />
          {/* Legacy representative manager — superseded by My Team (single
              source of truth: user-level permissions). Old links redirect. */}
          <Route path="/my-profile" element={<Navigate to="/my-team" replace />} />
          <Route
            path="/my-team"
            element={
              <PrivateRoute roles={['Client']}>
                <MyTeamPage />
              </PrivateRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <PrivateRoute>
                <ProfilePage />
              </PrivateRoute>
            }
          />
          {/* Single-tenant deployment: no marketing landing page. */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/contact" element={<ContactPage />} />
                </Routes>
                </Suspense>
                </MOMFormConfigProvider>
              </FormConfigProvider>
            </FileUploadConfigProvider>
            </AuthProvider>
          </BrandingProvider>
        </LanguageProvider>
      </TenantProvider>
    </Router>
  );
}

export default App;